import { client } from "./client.ts";
import { PASSPORT_BASE_URL, USER_BASE_URL } from "./internal-const.ts";
import { APP_DATA_SOURCE, APP_ID } from "@/env.ts";

export interface Product {
  source_product_id: string;
  platform: "Apple" | "Android" | "Stripe" | "Manual";
  product_id: string;
  old_platform?: string; // Migrated access
  old_product_id?: string; // Migrated access
  end_ts?: number; // Expiry
  purchase_date?: number;
}

export interface ResetPasswordResponse {
  email: string;
}

export interface VidAppJWT {
  iss: string;
  sub: string;
  exp: number;
  uid: string;
  app_id: string;
  user_name: string;
  email: string;
  products: string[];
  detailed_products: Product[];
}

export enum NOTIFICATION_TYPE {
  commentsAndReplies = "commentsAndReplies",
  newPosts = "newPosts",
  postByAdmins = "postByAdmins",
  reactions = "reactions",
}

export enum NOTIFICATION_CONTENT_TYPE {
  post = "post",
  comment = "comment",
  reply = "reply",
}

export interface CommunityNotificationPayload {
  notification_type: NOTIFICATION_TYPE;
  content_type: NOTIFICATION_CONTENT_TYPE;
  content_id: string;
  community_id: string;
  // Original Content: For commentsAndReplies, the content being replied to or commented on
  original_content_id?: string;
  original_content_type?: NOTIFICATION_CONTENT_TYPE;
}

const DEFAULT_ERROR =
  "Failed to sign in due to unexpected error, please try again later";
const INVALID_ERROR = "Invalid email or password, please try again";
const INVALID_REFRESH =
  "Failed to refresh token, invalid or expired credentials";
const DELETED_ERROR = "Cannot sign in to deleted user";
const REFRESH_ERROR = "Failed to refresh token due to unexpected error";

export const login = async (email: string, pass: string) => {
  const data = await client
    .post<{ token: string; expiry: number }>(
      `${PASSPORT_BASE_URL}/auth/token/new`,
      {
        data_source: APP_DATA_SOURCE,
        app_id: APP_ID,
        email: email,
        password: pass,
      },
      { withCredentials: true },
    )
    .catch((e) => {
      if (e.response?.status == 403) {
        throw new Error(INVALID_ERROR);
      }
      if (e.response?.data?.detail?.includes("deleted user")) {
        throw new Error(DELETED_ERROR);
      }
      if (e.response?.data?.detail?.includes("Too many attempts")) {
        throw new Error(e.response.data?.detail);
      }
      throw new Error(DEFAULT_ERROR);
    });

  const token = data?.data?.token;
  if (token) {
    localStorage.setItem("jwt", token);
    return token;
  }
  throw new Error(DEFAULT_ERROR);
};
export const refreshToken = async ({ user_id }: { user_id: string }) => {
  const data = await client
    .post<{
      token: string;
      expiry: number;
    }>(
      `${PASSPORT_BASE_URL}/auth/token/refresh`,
      {
        user_id,
        data_source: APP_DATA_SOURCE,
        app_id: APP_ID,
      },
      { withCredentials: true },
    )
    .catch(async (e) => {
      if ([400, 401, 403].includes(e.response?.status)) {
        console.error(INVALID_REFRESH);
        await logout({ user_id });
      }
    });
  if (data) {
    const token = data.data?.token;
    if (token) {
      localStorage.setItem("jwt", token);
      return token;
    }
  }
  console.error(REFRESH_ERROR);
  await logout({ user_id });
};

export const logout = async ({ user_id }: { user_id: string }) => {
  localStorage.removeItem("jwt");
  const response = await client.post(
    `${PASSPORT_BASE_URL}/auth/logout`,
    { app_id: APP_ID, user_id },
    { withCredentials: true },
  );
  window.location.href = window.location.origin;
  return response;
};

export const getStripePortal = async () => {
  const data = await client.post(`${USER_BASE_URL}/api/stripe-portal`);
  return data.data;
};

export const resetPassword = async (email: string) => {
  return client.post<ResetPasswordResponse>(
    `${PASSPORT_BASE_URL}/auth/reset-password`,
    {
      app_id: APP_ID,
      email,
    },
  );
};

export const updatePassword = async ({
  code,
  password,
}: {
  code: string;
  password: string;
}) => {
  return client.post(`${PASSPORT_BASE_URL}/auth/update-password`, {
    app_id: APP_ID,
    code,
    password,
  });
};

interface StripeCoupon {
  ID: string;
  Name: string;
  AmountOff: number | null;
  PercentOff: number | null;
  Duration: "forever" | "once" | "repeating";
  DurationInMonths: number | null;
}

interface StripeDiscount {
  ID: string;
  Coupon: StripeCoupon;
}
interface StripeSub {
  ID: string;
  PriceID: string;
  PriceAmount: number;
  ProductID: string;
  Status: string;
  Currency: string;
  Interval: string;
  IntervalCount: number;
  StartDate: number; // Unix timestamps
  TrialEnd: number | null;
  CurrentPeriodStart: number;
  CurrentPeriodEnd: number;
  CancelAtPeriodEnd?: boolean;
  Discount?: StripeDiscount;
}

interface StripeUser {
  CustomerID: string;
  Subscriptions: StripeSub[];
}

interface UserDetails {
  first_name: string;
  last_name?: string;
  email: string;
  avatar_url: string | null;
  marketing_opt_in: boolean;
}

export const getStripeUser = async () => {
  return client
    .get<StripeUser>(`${USER_BASE_URL}/api/stripe-user`)
    .then((data) => data.data);
};

export const emailExists = async ({ email }: { email: string }) => {
  return client
    .post(`${USER_BASE_URL}/api/exists`, {
      app_id: APP_ID,
      email: email,
    })
    .then((data) => data.data?.exists);
};

export const getUserDetails = async () => {
  return client
    .get<UserDetails>(`${USER_BASE_URL}/api/user-details`)
    .then((data) => data.data);
};

export const getAmityAuth = async () => {
  return client
    .get<{
      token: string;
    }>(`${USER_BASE_URL}/api/community-auth`, { params: { app_id: APP_ID } })
    .then((data) => data.data.token);
};

export const sendCommunityNotification = async (
  payload: CommunityNotificationPayload,
) => {
  return client
    .post(`${USER_BASE_URL}/api/notification`, payload)
    .then((data) => data.data);
};
